import { Funnel } from "@phosphor-icons/react";
import clsx from "clsx";
import { useActivityDataUnits } from "../../api/activity-data";
import { SortOrder } from "../../api/common/models";
import { Location } from "../../api/models/Location";
import { useLocations } from "../../api/location";
import { DataSourceOrderBy } from "../../api/models/DataSource";
import { emissionsFactorOptions, scopeOptions } from "../../constants";
import useCollapsibleContent from "../../hooks/useCollapsibleContent";
import Button from "../Button";
import DataSourceSortByDropdown from "../DataSourceSortByDropdown";
import FilterChip from "../FilterChip";
import MultiSelectDropdown, {
    MultiSelectDropdownItemOption,
} from "../MultiSelectFilterToolbarDropdown";
import ResponsiveGroupedItemsCarousel from "../ResponsiveGroupedItemsCarousel";
import SearchBar from "../SearchBar";
import Separator from "../Separator";
import SingleSelectDropdown from "../SingleSelectFilterToolbarDropdown";
import FilterChipGroup from "../FilterChipGroup";
import { toggleItemInArray } from "../../utils/toggleItemInArray";
import { ActivityDataUnit } from "../../api/models/ActivityData";

type DataSourcesFilterProps = {
    showSortBy?: boolean;
    locationIds: number[];
    scopes: number[];
    currentEmissionsFactorStatus: string | null;
    rawUnits: number[];
    search: string;
    orderBy: DataSourceOrderBy;
    sortOrder: SortOrder;
    additionalActions?: React.ReactNode;
    setLocationIds: (value: number[]) => void;
    setScopes: (value: number[]) => void;
    setCurrentEmissionsFactorStatus: (value: string | null) => void;
    setRawUnits: (value: number[]) => void;
    setSearch: (value: string) => void;
    setOrderBy: (value: DataSourceOrderBy) => void;
    setSortOrder: (value: SortOrder) => void;
    setPageSize: (value: number) => void;
    setPageNumber: (value: number) => void;
};

const convertLocationsToMultiSelectDropdownOptions = (
    locations: Location[]
): MultiSelectDropdownItemOption<number>[] =>
    locations.map((location) => ({
        label: location.name,
        value: location.id,
    }));

const convertActivityDataUnitsToMultiSelectDropdownOptions = (
    activityDataUnits: ActivityDataUnit[]
): MultiSelectDropdownItemOption<number>[] =>
    activityDataUnits.map((activityDataUnit) => ({
        label: activityDataUnit.suffix,
        value: activityDataUnit.id,
    }));

const DataSourcesFilter = ({
    showSortBy,
    search,
    setSearch,
    locationIds,
    setLocationIds,
    scopes,
    setScopes,
    rawUnits,
    setRawUnits,
    currentEmissionsFactorStatus,
    setCurrentEmissionsFactorStatus,
    orderBy,
    sortOrder,
    setOrderBy,
    setSortOrder,
    setPageSize,
    setPageNumber,
    additionalActions,
}: DataSourcesFilterProps) => {
    const { locations } = useLocations();
    const { activityDataUnits } = useActivityDataUnits();

    const { expanded, toggle, triggerProps, expandableContentProps } =
        useCollapsibleContent();

    const locationOptions =
        convertLocationsToMultiSelectDropdownOptions(locations);

    const activityDataUnitsOptions =
        convertActivityDataUnitsToMultiSelectDropdownOptions(activityDataUnits);

    const handleLocationSelect = (value: number) =>
        setLocationIds(toggleItemInArray(locationIds, value));

    const handleScopeSelect = (value: number) =>
        setScopes(toggleItemInArray(scopes, value));

    const handleRawUnitsSelect = (value: number) =>
        setRawUnits(toggleItemInArray(rawUnits, value));

    const clearAllFilters = () => {
        setSearch("");
        setLocationIds([]);
        setScopes([]);
        setRawUnits([]);
        setCurrentEmissionsFactorStatus(null);
    };

    const showClearAllButton =
        search !== "" ||
        locationIds.length > 0 ||
        scopes.length > 0 ||
        rawUnits.length > 0 ||
        currentEmissionsFactorStatus !== null;

    let filterDropdowns = [
        <MultiSelectDropdown
            label="Location"
            selected={locationIds}
            options={locationOptions}
            onSelect={handleLocationSelect}
            onClear={() => setLocationIds([])}
            key="filter-dropdown-location"
        />,
        <MultiSelectDropdown
            label="Scope"
            selected={scopes}
            options={scopeOptions}
            onSelect={handleScopeSelect}
            onClear={() => setScopes([])}
            key="filter-dropdown-scope"
        />,
        <MultiSelectDropdown
            label="Reported Units"
            selected={rawUnits}
            options={activityDataUnitsOptions}
            onSelect={handleRawUnitsSelect}
            onClear={() => setRawUnits([])}
            key="filter-dropdown-reported-units"
        />,
        <SingleSelectDropdown
            label="Emissions Factors"
            selected={currentEmissionsFactorStatus}
            options={emissionsFactorOptions}
            name="emissionsFactor"
            onSelect={setCurrentEmissionsFactorStatus}
            onReset={() => setCurrentEmissionsFactorStatus(null)}
            key="filter-dropdown-emissions-factors"
        />,
    ];

    if (showSortBy) {
        filterDropdowns = [
            <DataSourceSortByDropdown
                orderBy={orderBy}
                sortOrder={sortOrder}
                setOrderBy={setOrderBy}
                setSortOrder={setSortOrder}
                setPageNumber={setPageNumber}
                setPageSize={setPageSize}
                key="filter-dropdown-data-source-sort"
            />,
            ...filterDropdowns,
        ];
    }

    const currentEmissionsFactorStatusLabel = emissionsFactorOptions.find(
        (emissionsFactor) =>
            emissionsFactor.value === currentEmissionsFactorStatus
    )?.label;

    return (
        <>
            <div className="flex items-center p-5">
                <Button
                    variant="outline"
                    onClick={toggle}
                    {...triggerProps}
                    className="flex-none"
                >
                    <Funnel width={20} height={20} className="mr-2" />
                    {expanded ? "Hide" : "Show"} filters & sorting
                </Button>
                <div className="flex gap-2 flex-wrap pl-2">
                    {search !== "" && (
                        <FilterChip onRemove={() => setSearch("")}>
                            '{search}'
                        </FilterChip>
                    )}
                    <FilterChipGroup
                        selectedItemIds={locationIds}
                        allItems={locations}
                        labelKey="name"
                        onRemove={setLocationIds}
                    />
                    <FilterChipGroup
                        allItems={scopeOptions.map((scope) => ({
                            ...scope,
                            id: scope.value,
                        }))}
                        selectedItemIds={scopes}
                        labelKey="label"
                        onRemove={setScopes}
                    />
                    <FilterChipGroup
                        selectedItemIds={rawUnits}
                        allItems={activityDataUnits}
                        labelKey="suffix"
                        onRemove={setRawUnits}
                    />
                    {currentEmissionsFactorStatus !== null && (
                        <FilterChip
                            onRemove={() =>
                                setCurrentEmissionsFactorStatus(null)
                            }
                        >
                            {currentEmissionsFactorStatusLabel}
                        </FilterChip>
                    )}
                    {showClearAllButton && (
                        <Button variant="outline" onClick={clearAllFilters}>
                            Clear all
                        </Button>
                    )}
                </div>
                <div className="ml-auto flex-none">{additionalActions}</div>
            </div>
            <Separator />
            <div
                className={clsx("pt-5 px-6", !expanded && "hidden")}
                {...expandableContentProps}
            >
                <SearchBar
                    value={search}
                    onChange={setSearch}
                    placeholder="Search..."
                    additionalSearchElements={
                        <ResponsiveGroupedItemsCarousel
                            items={filterDropdowns}
                            prevTooltipLabel="See previous group of filters"
                            nextTooltipLabel="See next group of filters"
                        />
                    }
                />
            </div>
        </>
    );
};

export default DataSourcesFilter;
